import React, { useState, useEffect } from "react";
import {
  LegacyCard,
  Text,
  Banner,
  Button,
  InlineStack,
  Select,
  BlockStack,
  Box,
  Divider,
} from "@shopify/polaris";
import { useDispatch } from "react-redux";
import { AppDispatch } from "configureStore";
import { useTranslation } from "react-i18next";
import { setDestroy, setIsModalxOpen } from "components/modalx/modalx.slice";
import {
  fetchReminderInfo,
  updateReminderSettings,
} from "services/signature.service";
import { refreshRequested } from "containers/unsigned-files/unsigned-files.slice";
import Loading from "components/loading/Loading";
import PrettyDate from "components/format/PrettyDate";

interface ReminderSettingsModalProps {
  fileId: string;
  companyId: string;
  /**
   * Hide the editable "Reminder settings" section and show the history only.
   * Set by call sites that live outside a ProtectedRoute (the universal
   * document view), where any authenticated company member could otherwise
   * change the reminder schedule — the backend PATCH checks company ownership
   * only.
   */
  historyOnly?: boolean;
}

interface ReminderSettings {
  reminderIntervalDays: number | null;
  reminderEnabled: boolean;
}

interface ReminderRecipient {
  /**
   * Null once the Signatures row behind the reminder is gone — cancelling the
   * signing deletes it, while the reminder history survives on its own
   * snapshot (Desk #136).
   */
  signatureId: string | null;
  /** Both may be empty: the backend keeps a send event it can no longer name. */
  signerName: string;
  signerEmail: string;
}

interface Reminder {
  nr: number;
  date: string;
  recipients: ReminderRecipient[];
}

interface ReminderInfoResponse {
  reminderSettings: ReminderSettings;
  reminders: Reminder[];
}

const ReminderSettingsModal: React.FC<ReminderSettingsModalProps> = ({
  fileId,
  companyId,
  historyOnly = false,
}) => {
  const dispatch: AppDispatch = useDispatch();
  const { t } = useTranslation();

  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [reminderInfo, setReminderInfo] = useState<ReminderInfoResponse | null>(
    null
  );
  const [reminderEnabled, setReminderEnabled] = useState<boolean>(false);
  const [reminderIntervalDays, setReminderIntervalDays] = useState<
    number | null
  >(null);

  useEffect(() => {
    // Guard: only fetch data if both fileId and companyId are available
    if (fileId && companyId) {
      fetchData();
    }
  }, [fileId, companyId]);

  // Auto-dismiss success message after 3 seconds
  useEffect(() => {
    if (successMessage) {
      const timeoutId = setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);

      // Cleanup timeout on unmount or when successMessage changes
      return () => {
        clearTimeout(timeoutId);
      };
    }
  }, [successMessage]);

  const fetchData = async (
    suppressError: boolean = false,
    showLoading: boolean = true
  ) => {
    try {
      if (showLoading) {
        setLoading(true);
      }
      if (!suppressError) {
        setError(null);
      }
      const data: ReminderInfoResponse = await fetchReminderInfo(
        companyId,
        fileId
      );
      setReminderInfo(data);
      setReminderEnabled(data.reminderSettings.reminderEnabled);
      setReminderIntervalDays(data.reminderSettings.reminderIntervalDays);
      return true;
    } catch (err: any) {
      if (!suppressError) {
        setError(t("failed_to_load_reminder_data"));
      }
      return false;
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccessMessage(null);

      // Validate that days are selected if reminders are enabled
      if (reminderEnabled && reminderIntervalDays === null) {
        setError(t("days_between_reminders_required"));
        setSaving(false);
        return;
      }

      // Validate interval range if set (regardless of reminderEnabled state)
      if (reminderIntervalDays !== null) {
        if (reminderIntervalDays < 1 || reminderIntervalDays > 7) {
          setError(t("days_between_reminders_validation"));
          setSaving(false);
          return;
        }
      }

      await updateReminderSettings(companyId, fileId, {
        reminderIntervalDays,
        reminderEnabled,
      });

      // Refresh data silently in background (no loading screen) - if this fails, clear success message and show error
      const refreshSuccess = await fetchData(true, false); // Suppress error and don't show loading during refresh

      // Also reload the unsigned assigned documents list so tables reflect new reminder settings immediately
      dispatch(refreshRequested());

      if (!refreshSuccess) {
        // Clear success message if refresh fails to avoid showing both banners
        setSuccessMessage(null);
        setError(t("failed_to_load_reminder_data"));
      } else {
        // Set success message - useEffect will handle auto-dismiss after 3 seconds
        setSuccessMessage(t("reminder_settings_updated_successfully"));
      }
    } catch (err: any) {
      setError(t("failed_to_save_settings"));
    } finally {
      setSaving(false);
    }
  };

  const closeModal = () => {
    // Clear success message when closing modal (useEffect will clean up timeout)
    setSuccessMessage(null);
    dispatch(setIsModalxOpen(false));
    dispatch(setDestroy());
  };

  // A recipient can come back without a name, without an email, or without
  // both — the backend keeps such a send event rather than dropping it from the
  // history. Print whatever is left instead of an empty "• ()".
  const describeRecipient = (recipient: ReminderRecipient): string => {
    const name = (recipient.signerName || "").trim();
    const email = (recipient.signerEmail || "").trim();
    if (name && email) return `${name} (${email})`;
    return name || email || t("reminder_recipient_unknown");
  };

  const intervalOptions = [
    { label: " - ", value: "" },
    { label: `1 ${t("day")}`, value: "1" },
    { label: `2 ${t("days")}`, value: "2" },
    { label: `3 ${t("days")}`, value: "3" },
    { label: `4 ${t("days")}`, value: "4" },
    { label: `5 ${t("days")}`, value: "5" },
    { label: `6 ${t("days")}`, value: "6" },
    { label: `7 ${t("days")}`, value: "7" },
  ];

  if (loading) {
    return <Loading />;
  }

  return (
    <BlockStack gap="400">
      <div className="form-warnings">
        {error && (
          <div style={{ marginBottom: "16px" }}>
            <Banner tone="critical">{error}</Banner>
          </div>
        )}
        {successMessage && (
          <div style={{ marginBottom: "16px" }}>
            <Banner tone="success">{successMessage}</Banner>
          </div>
        )}
      </div>

      {/* Section 1: Reminder Settings — hidden for history-only call sites */}
      {!historyOnly && (
        <>
          <LegacyCard sectioned>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">
                {t("reminder_settings")}
              </Text>

              <Box paddingBlockStart="400">
                <label
                  htmlFor="reminderEnabled"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    cursor: "pointer",
                  }}
                >
                  <input
                    type="checkbox"
                    id="reminderEnabled"
                    checked={reminderEnabled}
                    onChange={(e) => {
                      setReminderEnabled(e.target.checked);
                      if (!e.target.checked) {
                        // Clear interval when disabling reminders
                        setReminderIntervalDays(null);
                      }
                    }}
                    style={{
                      width: "18px",
                      height: "18px",
                      cursor: "pointer",
                    }}
                  />
                  <Text as="span">{t("enable_automatic_reminders")}</Text>
                </label>
              </Box>

              <Box paddingBlockStart="200">
                <Select
                  label={t("days_between_reminders")}
                  options={intervalOptions}
                  value={
                    reminderIntervalDays !== null
                      ? reminderIntervalDays.toString()
                      : ""
                  }
                  onChange={(value) => {
                    setReminderIntervalDays(
                      value === "" ? null : parseInt(value, 10)
                    );
                  }}
                  disabled={!reminderEnabled}
                />
              </Box>

              <Box paddingBlockStart="400">
                <Button
                  onClick={handleSaveSettings}
                  loading={saving}
                  variant="primary"
                  disabled={
                    !reminderInfo ||
                    (reminderEnabled && reminderIntervalDays === null) ||
                    saving
                  }
                >
                  {t("save_settings")}
                </Button>
              </Box>
            </BlockStack>
          </LegacyCard>

          <Divider />
        </>
      )}

      {/* Section 2: Sent Reminders List */}
      <LegacyCard sectioned>
        <BlockStack gap="400">
          <Text as="h2" variant="headingMd">
            {t("sent_reminders")}
          </Text>

          {reminderInfo && reminderInfo.reminders.length > 0 ? (
            <BlockStack gap="400">
              {reminderInfo.reminders.map((reminder) => (
                <Box
                  key={reminder.nr}
                  padding="400"
                  background="bg-surface-secondary"
                  borderRadius="200"
                  borderWidth="025"
                  borderColor="border"
                >
                  <BlockStack gap="200">
                    <Text as="p" variant="bodyMd" fontWeight="semibold">
                      {t("reminder_number")} {reminder.nr}
                    </Text>
                    <Text as="p" variant="bodyMd">
                      {t("reminder_date")}{" "}
                      <PrettyDate dateString={reminder.date} showTime />
                    </Text>
                    <Box paddingBlockStart="200">
                      <Text as="p" variant="bodyMd" fontWeight="semibold">
                        {t("reminder_recipients")}
                      </Text>
                      <Box paddingBlockStart="100">
                        <BlockStack gap="100">
                          {reminder.recipients.map((recipient, index) => (
                            <Text key={index} as="p" variant="bodySm">
                              • {describeRecipient(recipient)}
                            </Text>
                          ))}
                        </BlockStack>
                      </Box>
                    </Box>
                  </BlockStack>
                </Box>
              ))}
            </BlockStack>
          ) : (
            // Always say something here. The history is the reason the document
            // view opens this modal, so a silent section would read as broken —
            // and it must stay visible whether reminders are on or off.
            //
            // The one exception is a failed load: `error` is already on screen
            // as a critical banner above, so repeating the same sentence here
            // would only say it twice.
            !(!reminderInfo && error) && (
              <Box paddingBlockStart="200">
                <Text as="p" variant="bodyMd" tone="subdued">
                  {reminderInfo
                    ? t("no_reminders_sent_yet")
                    : t("failed_to_load_reminder_data")}
                </Text>
              </Box>
            )
          )}
        </BlockStack>
      </LegacyCard>

      <Box paddingBlockStart="400">
        <InlineStack align="end">
          <Button onClick={closeModal}>{t("close")}</Button>
        </InlineStack>
      </Box>
    </BlockStack>
  );
};

export default ReminderSettingsModal;
